import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ActionCausedByRole } from './eventHistory';
import { queryDelete, queryUpdate, queryCreate, queryGet } from './fetch';

export interface DocumentAttachment {
  sessionId?: string;
  participationId?: string;
}

export interface PrepareUploadDto {
  contentType: string;
  extension: string;
  originalName: string;
}

export enum DocumentType {
  IMAGE = 'image',
  PDF = 'pdf',
  FILE = 'file',
}

export interface Document {
  id: string;
  originalName: string;
  filename: string;
  path: string;
  mimeType: string;

  type: DocumentType;
  uploadedById: string;
  uploadedByRole: ActionCausedByRole;

  participationId?: string;
  sessionId: string;
  organizationId: string;

  uploadConfirmed: boolean;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
}

const getPrefixUrl = ({ sessionId, participationId }: DocumentAttachment) => {
  return participationId
    ? `/participations/${participationId}/documents`
    : `/sessions/${sessionId}/documents`;
};

const getPrefixKey = (att: DocumentAttachment) => {
  return getPrefixUrl(att).split('/');
};

export const useCreateDocumentUploadUrl = (attachment: DocumentAttachment) => {
  return useMutation<
    { url: string; document: Document },
    unknown,
    PrepareUploadDto
  >(queryCreate(getPrefixUrl(attachment)));
};

export const useDocuments = (attachment: DocumentAttachment) => {
  return useQuery<Document[]>(
    getPrefixKey(attachment),
    queryGet(getPrefixUrl(attachment)),
  );
};

export const useClearDocumentCache = (attachment: DocumentAttachment) => {
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient.invalidateQueries(getPrefixKey(attachment));
  }, [queryClient, attachment]);
};

export const useConfirmUpload = (attachment: DocumentAttachment) => {
  const clearCache = useClearDocumentCache(attachment);
  return useMutation<Document, unknown, string>(
    (id: string) =>
      queryUpdate(`${getPrefixUrl(attachment)}/${id}/confirm`)({}),
    {
      onSuccess: () => clearCache(),
    },
  );
};

export const useRemoveDocument = (document?: Document) => {
  const clearCache = useClearDocumentCache(document || {});
  return useMutation<Document>(
    queryDelete(document ? `${getPrefixUrl(document)}/${document.id}` : ''),
    {
      onSuccess: () => clearCache(),
    },
  );
};

export const useGetViewDocumentUrl = (document?: Document) => {
  return useMutation<{ url: string; document: Document }>(
    queryGet(
      document ? `${getPrefixUrl(document)}/${document.id}/view-url` : '',
    ),
  );
};
